import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { ImportModal } from "@dashboard/imports/components/ImportModal/ImportModal";
import { ImportPipelineChain } from "@dashboard/imports/components/ImportPipelineChain/ImportPipelineChain";
import { ImportType } from "@dashboard/imports/types";
import { sectionNames } from "@dashboard/intl";
import { Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { messages } from "./messages";

const PIPELINE_STEPS = [
  {
    sheetName: "categories",
    displayName: "Categories",
    position: 1,
    dependsOn: [],
    fieldPreview: [
      { name: "name", description: "Category name", required: true },
      { name: "slug", description: "URL slug", required: true },
      { name: "description", description: "Description", required: false },
      { name: "parent_slug", description: "Parent category slug", required: false },
      { name: "seo_title", description: "SEO title", required: false },
      { name: "seo_description", description: "SEO description", required: false },
    ],
  },
  {
    sheetName: "attributes",
    displayName: "Attributes",
    position: 2,
    dependsOn: [],
    fieldPreview: [
      { name: "name", description: "Attribute name", required: true },
      { name: "slug", description: "URL slug", required: true },
      {
        name: "input_type",
        description:
          "Input type (DROPDOWN, MULTISELECT, BOOLEAN, NUMERIC, SWATCH, PLAIN_TEXT, RICH_TEXT, DATE, DATE_TIME, FILE, REFERENCE)",
        required: true,
      },
      {
        name: "values",
        description:
          "Comma-separated list of predefined values (for DROPDOWN / MULTISELECT / SWATCH)",
        required: false,
      },
      { name: "unit", description: "Unit for NUMERIC attributes (e.g. kg, cm)", required: false },
      {
        name: "value_required",
        description: "Whether a value is required (true/false)",
        required: false,
      },
      {
        name: "visible_in_storefront",
        description: "Visible in storefront (true/false)",
        required: false,
      },
      {
        name: "filterable_in_storefront",
        description: "Filterable in storefront (true/false)",
        required: false,
      },
      {
        name: "filterable_in_dashboard",
        description: "Filterable in dashboard (true/false)",
        required: false,
      },
    ],
  },
  {
    sheetName: "product_types",
    displayName: "Product Types",
    position: 3,
    dependsOn: [],
    fieldPreview: [
      { name: "name", description: "Product type name", required: true },
      { name: "slug", description: "URL slug", required: true },
      { name: "has_variants", description: "Has variants", required: false },
      { name: "is_shipping_required", description: "Requires shipping", required: false },
      { name: "weight", description: "Weight (kg)", required: false },
      { name: "tax_class", description: "Tax class name (e.g. Normal)", required: false },
      {
        name: "label_marks",
        description:
          "Label marks: ce,weee,rohs,recycle, GHS pictograms (e.g. ghs02,ghs07) or none; blank = automatic",
        required: false,
      },
    ],
  },
  {
    sheetName: "product_type_attributes",
    displayName: "Product Type Attributes",
    position: 4,
    dependsOn: ["Attributes", "Product Types"],
    fieldPreview: [
      { name: "product_type_slug", description: "Product type slug", required: true },
      { name: "attribute_slug", description: "Attribute slug", required: true },
      {
        name: "type",
        description:
          "Assignment level: PRODUCT_TYPE (product-level) or VARIANT_TYPE (variant-level)",
        required: true,
      },
    ],
  },
  {
    sheetName: "collections",
    displayName: "Collections",
    position: 5,
    dependsOn: [],
    fieldPreview: [
      { name: "name", description: "Collection name", required: true },
      { name: "slug", description: "URL slug", required: true },
      { name: "description", description: "Description", required: false },
      { name: "is_published", description: "Published", required: false },
      { name: "seo_title", description: "SEO title", required: false },
    ],
  },
  {
    sheetName: "products",
    displayName: "Products",
    position: 6,
    dependsOn: ["Categories", "Product Types"],
    fieldPreview: [
      { name: "name", description: "Product name", required: true },
      { name: "slug", description: "URL slug", required: true },
      { name: "product_type_slug", description: "Product type", required: true },
      { name: "category_slug", description: "Category", required: true },
      { name: "description", description: "Description", required: false },
      { name: "weight", description: "Weight (kg)", required: false },
      {
        name: "tax_class",
        description: "Tax class name; overrides product type's",
        required: false,
      },
      { name: "seo_title", description: "SEO title", required: false },
      { name: "seo_description", description: "SEO description", required: false },
      {
        name: "label_marks",
        description:
          "Label marks stored on the product (e.g. ghs02,ghs07,ghs09 from the SDS); blank = the type's",
        required: false,
      },
    ],
  },
  {
    sheetName: "collection_products",
    displayName: "Collection Products",
    position: 7,
    dependsOn: ["Collections", "Products"],
    fieldPreview: [
      { name: "collection_slug", description: "Collection slug", required: true },
      { name: "product_slug", description: "Product slug", required: true },
    ],
  },
  {
    sheetName: "channel_listings",
    displayName: "Channel Listings",
    position: 8,
    dependsOn: ["Products"],
    fieldPreview: [
      { name: "product_slug", description: "Product slug", required: true },
      { name: "channel_slug", description: "Channel slug", required: true },
      { name: "is_published", description: "Published", required: false },
      { name: "price_amount", description: "Price", required: false },
      { name: "currency", description: "Currency", required: false },
    ],
  },
  {
    sheetName: "variants",
    displayName: "Variants",
    position: 9,
    // Variants attach to an existing parent product — import the Products sheet first.
    dependsOn: ["Products"],
    fieldPreview: [
      { name: "sku", description: "SKU", required: true },
      { name: "product_slug", description: "Parent product slug (must exist)", required: true },
      { name: "name", description: "Variant name", required: false },
      { name: "weight", description: "Weight (kg)", required: false },
      {
        name: "channel_slug",
        description: "Publish & price the variant in a channel in one pass",
        required: false,
      },
      { name: "price", description: "Variant price (with channel_slug)", required: false },
      {
        name: "quantity",
        description: "Blank = unlimited/always purchasable; a number = tracked & capped",
        required: false,
      },
    ],
  },
  {
    sheetName: "variant_channel_listings",
    displayName: "Variant Channel Listings",
    position: 10,
    dependsOn: ["Variants"],
    fieldPreview: [
      { name: "sku", description: "SKU", required: true },
      { name: "channel_slug", description: "Channel slug", required: true },
      { name: "price_amount", description: "Price", required: true },
      { name: "currency", description: "Currency", required: true },
    ],
  },
  {
    sheetName: "images",
    displayName: "Images",
    position: 11,
    dependsOn: ["Products", "Variants"],
    fieldPreview: [
      { name: "product_slug", description: "Product slug", required: false },
      { name: "sku", description: "Variant SKU", required: false },
      { name: "image_url", description: "Image URL or filename", required: true },
      { name: "alt", description: "Alt text", required: false },
    ],
  },
  {
    sheetName: "shipping_rates",
    displayName: "Shipping Rates",
    position: 12,
    // Optional: only needed when the exclusion columns are used. A rate can
    // reference excluded products and/or excluded product types — either source
    // is optional, so neither is strictly required. Single "and/or" entry so the
    // card doesn't read as requiring both.
    dependsOn: ["Products and/or Product Types"],
    fieldPreview: [
      { name: "zone_name", description: "Shipping zone (created if missing)", required: true },
      { name: "rate_name", description: "Rate / tier name", required: true },
      { name: "type", description: "WEIGHT or PRICE", required: true },
      { name: "channel_slug", description: "Channel slug", required: true },
      { name: "price", description: "Rate price (0 = free shipping)", required: true },
      {
        name: "min_order_value",
        description: "Free-shipping threshold (use with price 0)",
        required: false,
      },
      { name: "tax_class", description: "Tax class name for this rate", required: false },
      {
        name: "excluded_product_type_slugs",
        description: "Product types excluded from this rate",
        required: false,
      },
    ],
  },
];

export const ImportOverview = () => {
  const intl = useIntl();
  const [activeImportType, setActiveImportType] = useState<ImportType | null>(null);

  const steps = PIPELINE_STEPS;

  return (
    <>
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.imports)} withoutBorder />
        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Title>
              <FormattedMessage {...messages.pipelineTitle} />
            </DashboardCard.Title>
          </DashboardCard.Header>
          {/* paddingX matches DashboardCard.Content default (6 = 24px) */}
          <div style={{ paddingLeft: 24, paddingRight: 24 }}>
            <div style={{ marginBottom: 12 }}>
              <Text size={2} color="default2">
                <FormattedMessage {...messages.pipelineDescription} />
              </Text>
            </div>
            <ImportPipelineChain
              steps={steps}
              onImport={importType => setActiveImportType(importType)}
            />
          </div>
        </DashboardCard>
      </ListPageLayout>

      <ImportModal
        open={activeImportType !== null}
        importType={activeImportType ?? ImportType.PRODUCTS}
        onClose={() => setActiveImportType(null)}
      />
    </>
  );
};

export default ImportOverview;
